// ─────────────────────────────────────────────────────────────────────────────
// 🔑 BORROWER-FACING COPY FOR THE GUIDED STEPS, AS DATA.
//
// 🚨 WHY IT IS NOT INLINE JSX. These sentences are the ones a test has to be
// able to read: "step 6 never says approved", "the hard inquiry is disclosed
// before the consent screen", "the step-5 line tells them what to have handy".
// Asserting a sentence through a component means asserting a regex over JSX,
// which pins the markup rather than the words — and the loader that evaluates
// frontend constants refuses a component, correctly.
//
// ⚠️ IT IS NOT A COPY DECK AND MUST NOT BECOME ONE. Only strings that something
// else has to reason about belong here; a heading nobody asserts is better off
// where it is rendered.
// ─────────────────────────────────────────────────────────────────────────────

// ── step 5 ──────────────────────────────────────────────────────────────────
// Jesse, 2026-10-22: "account statements" (was "bank statements").
export const STEP5_READY_LINE = 'Have your recent pay stubs and account statements handy.';

// ── step 6 ──────────────────────────────────────────────────────────────────
//
// 🚨 TWO SENTENCES, IN ORDER: the credit check, then the decision. The hard
// inquiry is named HERE as well as in the authorization, because a borrower
// should not first learn what they are agreeing to on the screen where they
// agree to it.
//
// ⚠️ NEITHER SENTENCE SAYS "APPROVED". Step 6 is the one place in the flow where
// the news is unambiguously good and therefore most likely to be over-read; a
// borrower told "approved" here has been told they have a loan, two steps before
// a decision exists and while a decline is still possible.
export const STEP6_NEXT = Object.freeze([
  'Next we check your credit. You will authorize that on the next screen, and it is a '
  + 'hard inquiry that will show on your credit report.',
  'After that we can give you a decision.',
]);

// ── 🚨 STEP 6: THE FIGURE, AND THE QUESTION ABOUT IT ────────────────────────
//
// 🚨 THE CONFIRMATION HAD NO SCREEN. POST /api/readycheck/pull-credit refuses
// unless users.readycheck_phase2_completed_at is set, and only /confirm-income
// sets it — so every borrower in the shell reached step 7, authorized a hard
// inquiry, and got "Please complete income confirmation before pulling credit".
// Phase 6 depends on the same act: Atlas may use the figure only when the
// section is verified AND the borrower has confirmed it.
//
// ⚠️ THE QUESTION IS ASKED PLAINLY AND THE DISAGREEMENT IS NOT PUNISHED. A
// borrower who says the figure is wrong is often right — the engine reads pay
// stubs, not a life — and the path that follows has to look like a conversation
// rather than a refusal. It routes to a person; it does not stop the file.
export const STEP6_INCOME = Object.freeze({
  heading: 'The income we will use',
  // 🔑 "WE WILL USE" IS THE HONEST FRAME. It is not their salary, and it is not
  // a decision; it is the figure the next step is priced from.
  lead: 'This is the monthly income we worked out from what you sent. '
    + 'It is what we will use to work out your decision.',
  // ⚠️ THE OVERRIDE IS NOT EXPLAINED TO THE BORROWER as an override. From their
  // side it is simply the figure Doorly is using, which is what it is.
  confirm: 'That looks right',
  dispute: 'That does not look right',
  // 🚨 TRUTHFUL ABOUT THE HOLD (2026-10-16). It said "Your application carries on in the
  // meantime", but the credit check is held until the dispute is settled.
  disputeLead: 'Tell us what looks wrong. We will ask you for anything that shows it on step 5, '
    + 'and your figure updates once it\'s checked. Your credit check waits until this is settled.',
  disputePlaceholder: 'For example: my overtime is not included, or I changed jobs in June.',
  disputeSend: 'Send this',
  // 🔑 NO PROMISE WITHOUT A SEND (Jesse, 2026-10-25): the unused "disputed" line is gone; "missing" said "we will let
  // you know the moment it is ready" and nothing sends that.
  missing: 'We do not have a monthly income figure for you yet. We are finishing that off, '
    + 'and it will show here as soon as it is ready.',
});

// ── 🚨 STEP 6: THE THREE CHOICES (Jesse, 2026-10-16) ───────────────────────────
// Confirm, "Not quite right" (income or savings and accounts, separately) and "Add a co-borrower" —
// the classic ReadyCheck Step 2 choices, restored on the guided step that replaced it.
export const STEP6_CHOICES = Object.freeze({
  notQuiteRight: 'Not quite right',
  whichLead: 'Which figure does not look right?',
  whichIncome: 'My income',
  whichAssets: 'My savings and accounts',
  disputeOpen: (kind) => `You told us your ${kind === 'assets' ? 'savings and accounts' : 'income'} did not look right. `
    + 'We have asked you for anything that shows it on step 5, and your figure updates once it\'s checked. '
    + 'Your credit check waits until this is settled.',
  toStep5: 'Go to step 5',
  addCoborrower: 'Add a co-borrower',
  addCoborrowerLead: 'Applying with someone else? Invite them here. They make their own account, '
    + 'complete their own steps, and your credit is checked together once you have both agreed.',
  inviteSend: 'Send the invitation',
  inviteSent: (name) => `We have invited ${name}. Your application is now a joint application: `
    + 'the credit check waits until they have joined and you have both agreed to it.',
  withdraw: 'Withdraw the invite and continue on my own',
  withdrawLead: 'If you withdraw the invitation, your application continues with you as the only borrower.',
  requestChange: 'Request a change',
  requestChangeLead: (name) => `${name} has already joined your application. If something needs to change, `
    + 'tell us what it is here.',
  requestChangeSent: 'Thanks — we have your request.',
});

// ── 🚨 STEP 7: WAITING FOR AN INVITED CO-BORROWER (Jesse, 2026-10-16) ──────────
// 🔑 STEP 7'S HEADINGS, ONE COPY (Jesse, 2026-10-23): GuidedCreditStep renders them and the staff Review-tab header
// (constants/guidedRemaining) quotes them — the borrower's own wording, never a staff paraphrase.
export const STEP7_LINES = Object.freeze({
  jointIntent: 'Applying together',
  disputeOpen: 'Not yet — we are looking at the figure you told us was not right',
  notConfirmed: 'Not yet — we are still confirming your income and savings',
  declined: 'Nothing has been checked',
  checking: 'Checking your credit',
  reuse: 'We already have your credit report',
  reissue: 'Use the credit report already on your file',
  authorize: 'Authorize the credit check',
  waitingOn: (name) => `Waiting on ${name}`,
  waitingOnMany: 'Waiting on the other borrowers',
  // 🔑 Authorized alone, then a co-borrower joined (Jesse, 2026-10-25): the solo authorization does not cover the joint report.
  reauthorizeForJoint: (name) => `Your application now includes ${name}. Please confirm your authorization for the joint credit check.`,
  conditions: 'A few more things',
  // 🔑 The other borrower declined; this one authorized (walkthrough item 7, Jesse 2026-10-25).
  otherDeclined: (names) => `${joinNames(names)} ${names && names.length > 1 ? 'have' : 'has'} not authorized the credit check`,
  otherDeclinedBody: (names) => `Your authorization is recorded. A joint credit check needs every borrower to authorize it, `
    + `so nothing has been checked and no inquiry was made. If ${joinNames(names)} ${names && names.length > 1 ? 'change their minds, they' : 'changes their mind, they'} `
    + 'can authorize it from their own account.',
  otherDeclinedHelp: 'You can message our team from the menu if you want to talk about your options.',
  // 🔑 The check is running (walkthrough item 6): its own words, never "We could not complete the credit check".
  stillRunningCheck: 'Check again',
});

function joinNames(names) {
  const n = (Array.isArray(names) ? names : []).filter(Boolean);
  if (!n.length) return 'The other borrower';
  return n.length === 1 ? n[0] : `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
}

export const STEP7_INVITE_PENDING = Object.freeze({
  heading: (name) => `Waiting on ${name} to join`,
  body: 'You invited a co-borrower, so this is a joint application. Nothing has been checked: '
    + 'the credit check runs once they have joined, finished their own steps and agreed to it on their own account.',
  expired: 'The invitation has expired. You can send it again, or continue on your own.',
  resend: 'Send the invitation again',
});

// ── step 8 ──────────────────────────────────────────────────────────────────
//
// 🚨 FOUR OUTCOMES, FOUR VOCABULARIES, AND THEY MUST NOT BLEED INTO EACH OTHER.
// An approval may say "approved"; a review may not say anything decision-shaped
// at all, because no decision has been made; a decline says what it is and
// points at the notice that carries the reasons in the form the law requires.
//
// ⚠️ "ReadyCheck" APPEARS NOWHERE. The shell has never named the product to a
// borrower, and the decision screen is the one most likely to be screenshotted.
// 🔑 STEP 8'S "CHECKING WHAT YOU ADDED" STATE (the increase path) — Hannah-approved 2026-11-01, exactly.
export const ADD_FUNDS_CHECKING = Object.freeze({
  title: 'We’re checking what you added.',
  body: (amount) => `This usually takes a minute or two. Your current approval of ${amount} stays in place while we check.`,
});

export const STEP8_COPY = Object.freeze({
  approved: Object.freeze({
    heading: "You're approved",
    // 🔑 THE AMOUNT IS STATED PLAINLY AND IS NOT CALLED "what you asked for".
    // On an approval below the requested amount this same screen is shown, and a
    // sentence implying the figure is what they requested would be false there.
    // 🔑 ONE APPROVAL WORDING (Jesse, 2026-10-28): every approval says "subject to the conditions in your approval".
    body: 'This is the maximum purchase price Doorly can finance for you, based on the '
      + 'income, savings and credit we verified, subject to the conditions in your approval.',
    cta: 'See your SmartEstimate',
    // 🔑 SIGNING ON STEP 8 (the fold, 2026-10-22).
    signHeading: 'Your SmartEstimate',
    signBody: 'Review your SmartEstimate, then tap Accept SmartEstimate to read and sign the program agreement and start home shopping.',
    // 🔑 THE BUYING-POWER BOX (the approved step-8 mockups, Jesse 2026-10-25): only when the approval is below the price
    // asked for at step 2 (the decision's snapshot); the copy follows what limits it. No co-borrower tip (Jesse). The
    // "Add savings or a gift" re-run waits for its own plan and mockups.
    buyingPower: Object.freeze({
      heading: 'Your buying power',
      explain: Object.freeze({
        cash_to_close: (applied, approved) => `You applied for ${applied}. The savings we verified cover the down payment and closing costs on a price of up to ${approved}.`,
        // 🔑 RESERVES ARE ENFORCED (Jesse, 2026-10-25): when the file needs them, the sentence names them.
        cash_to_close_reserves: (applied, approved) => `You applied for ${applied}. The savings we verified cover the down payment, closing costs and the reserves your loan requires on a price of up to ${approved}.`,
        dti: (applied, approved) => `You applied for ${applied}. Based on the income and monthly debts we verified, you're approved for up to ${approved}. Your monthly debts set how large a payment you can qualify for.`,
        loan_tier: (applied, approved) => `You applied for ${applied}. Based on your credit profile, the largest loan your program allows supports a price of up to ${approved}.`,
        other: (applied, approved) => `You applied for ${applied}. Based on the income, savings and credit we verified, you're approved for up to ${approved}.`,
      }),
      waysHeading: 'Ways to raise it:',
      ways: Object.freeze({
        cash_to_close: Object.freeze(['Add savings we have not counted yet', 'Add a gift from an eligible family member', 'Choose a slightly lower price, which lowers the cash you need at closing']),
        dti: Object.freeze(['Pay down a monthly debt, such as a credit card, car loan or student loan', 'Tell us about income we have not counted yet']),
        // (np-step5, the mockup review: "Choose a price within your approved range" removed — it raises nothing; as the FAQ)
        loan_tier: Object.freeze(['Improve your credit score to reach a higher loan tier']),
        other: Object.freeze([]),
      }),
      // 🔑 THE ADD-SAVINGS RE-RUN (screens 4-6): only on a funds-limited approval, unsigned.
      addFunds: 'Add savings or a gift →',
      addFundsNote: "We'll check what you add and update your approval. Your current approval stays in place while we do.",
      raised: (from, to) => `Your approval went up from ${from} to ${to} with what you added.`,
      unchanged: (max) => `Your approval stays at ${max}. What we verified doesn't raise it yet. You can add more, or continue with this approval.`,
      addMore: 'Add more savings →',
      questionsLead: 'Questions?',
      questionsLink: 'Send us a message',
      questionsTail: ". We're happy to walk through your options.",
    }),
    // 🔑 THE OPTIONAL "APPROVED FOR MORE" NOTE: only when the maximum is above the step-2 price.
    more: Object.freeze({
      heading: "You're approved for more than you asked for",
      body: (applied, approved) => `You applied for ${applied}. Your verified income, savings and credit support up to ${approved}.`,
      sub: "You don't have to use all of it. Choose the price that feels right below.",
    }),
    // 🔑 AFTER SIGNING: the locked terms (the display rule), one line when the rate is not the decision's.
    lockedHeading: 'Your signed SmartEstimate',
    rateChanged: 'Rate updated for the price and discount points you chose.',
    // 🔑 BOTH APPLICANTS SIGN (Part 1 item 1, Jesse 2026-10-25): the primary signs first and locks the figures; the
    // co-borrower then signs the same figures here. The "let you know" is the in-app alert and push accept-terms sends.
    coBorrowerSigns: (name) => `${name || 'Your co-borrower'} reviews and signs the SmartEstimate first. Then you read and sign the same figures here. We will let you know when it is ready for you.`,
    signedWaiting: (names) => `You have signed. We are waiting on ${(names && names.length ? names.join(' and ') : 'your co-borrower')} to sign the same figures. You can browse homes now; you can make an offer once everyone has signed.`,
    browseHomes: 'Browse homes',
    signedBody: 'Your SmartEstimate is signed.',
    signedCta: 'Go to your dashboard',
  }),
  declined: Object.freeze({
    heading: "We can't approve your application right now",
    // 🔑 Jesse, 2026-10-22: the reasons are the notice's (GET /step8); none mappable → the second line.
    body: 'The main reasons are below. Your formal notice has the full detail.',
    bodyNoReasons: 'Your formal notice explains the reasons.',
    // ⚠️ "right now" IS DELIBERATE AND IS NOT A PROMISE. It is true — a declined
    // file can be re-run on new figures — and it avoids the two wrong readings:
    // that the decision is permanent, and that it will certainly change.
    noticeLead: "We've sent your formal notice to",
    noticeTail: 'and saved it in your Documents.',
    viewNotice: 'View notice',
    message: 'Message our team',
    // 🔑 APPLYING AGAIN (Jesse, 2026-10-22 — backend constants/adverseReapply.reapplyStatus states the date).
    reapplyHeading: 'Applying again',
    reapplyOn: (date) => `You can apply again on ${date}.`,
    reapplyNow: 'You can apply again now.',
    reapplyCorrected: 'If something on your credit report was wrong and has been corrected, message our team and it can be checked again sooner.',
    reapplyScoreFloor: (n) => `Our minimum credit score is ${n}.`,
    reapplyContact: 'Message our team about applying again.',
    reapplyStart: 'Start a new application',
  }),
  // 🔑 The co-borrower was declined on their own credit (Part 1 item 2, Jesse 2026-10-25). First name only — nothing
  // of theirs: not the reason, not that a notice went to them.
  coborrowerChoice: Object.freeze({
    heading: (name) => `${name || 'Your co-borrower'} can't be added to this application`,
    body: 'Continue on your own, or stop here? If you continue, we decide your application on your own income, savings and credit.',
    continue: 'Continue on my own',
    stop: 'Stop here',
    continueGo: 'Confirm and continue',
    stopLead: 'Your application will be withdrawn and nothing more will be done with it. You can start a new application later.',
    stopGo: 'Withdraw my application',
    back: 'Go back',
    working: 'Saving…',
  }),
  withdrawn: Object.freeze({
    heading: 'Your application is withdrawn',
    body: 'Nothing more will be done with it. If you want to apply again, start a new application or message our team.',
  }),
  pending: Object.freeze({
    heading: 'Waiting on your lender',   // Hannah, 2026-11-01 (was "Not quite ready", before that "Not quite yet"): step 8's waiting state (a), the lender's VOM
    // 🔑 THE GENERIC TEXT (Jesse, 2026-10-29 — exactly this sentence, everywhere): no reason. "We'll send you an alert"
    // is kept: every path that ends a held state records a decision through utils/credit/atlasDecisionRouter, which sends
    // "Your decision is ready" (backend utils/decisionNotices) once — or, for a decline, the notice and its own alert. When the only
    // holds are borrower-safe waits (their lender's VOM) and no staff-only hold is active, the server sends its own line
    // instead (GET /step8 pendingLine — backend utils/decisionHolds), which names the wait and says an alert follows
    // (utils/decisionNotices sends it).
    body: "Your decision will show here as soon as it's ready, and we'll send you an alert.",
  }),
});

// 🔑 THE PROGRAM-FLOOR WARNING BEFORE STEP 7 (Jesse, 2026-10-25 — the assets write-up): verified savings below the cash
// the smallest financeable purchase needs (down payment + closing costs incl. the Program Fee + required reserves,
// backend utils/assetPlan). A warning with a choice — never a block.
const usd0 = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
export const FLOOR_WARNING = Object.freeze({
  heading: 'Your savings may not be enough to buy yet',
  body: (floor) => `The smallest home Doorly can finance is ${usd0(floor.price)}. With a ${floor.downPaymentPct}% down payment, closing costs and the Program Fee${floor.reservesMonths ? `, and ${floor.reservesMonths} months of reserves` : ''}, that needs about ${usd0(floor.cash)} in verified savings.`,
  verified: (v) => `Your verified savings are ${usd0(v)}.`,
  choice: "The next step checks your credit, which is a hard inquiry. You can add an account or a gift first, or continue and we'll make a decision on what's verified.",
  add: 'Add savings or a gift',
  proceed: 'Continue to step 7',
  addNote: '"Add savings or a gift" takes you back to the Assets section of step 5.',
});

// ─────────────────────────────────────────────────────────────────────────────
// 🔑 JOINT PART 2 (Jesse, 2026-10-27; plan "Joint Flow Part 2 Plan", approved). First names only — never "the primary
// borrower" — and nothing about another applicant's documents, figures or score.
// ─────────────────────────────────────────────────────────────────────────────
const joinFirstNames = (names) => {
  const n = (names || []).filter(Boolean);
  if (!n.length) return 'your co-borrower';
  return n.length === 1 ? n[0] : `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
};
const clockTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase(); } catch (_) { return null; }
};
export const JOINT_COPY = Object.freeze({
  // A · the progress card
  cardHeading: (names) => `Your application with ${joinFirstNames(names)}`,
  you: 'You',
  stepLine: (n, name) => `Step ${n} · ${name}`,
  keepGoing: "You can keep going. We check credit once you've both authorized it on step 7.",
  keepGoingMany: "You can keep going. We check credit once you've all authorized it on step 7.",
  remind: (name) => `Send ${name} a reminder`,
  reminderSent: (iso) => `Reminder sent${clockTime(iso) ? ` at ${clockTime(iso)}` : ''}. You can send another tomorrow.`,
  reminderRateLimited: (iso) => `You sent a reminder${clockTime(iso) ? ` at ${clockTime(iso)}` : ''}. You can send another tomorrow.`,
  reminderNothing: 'Nothing to remind about.',
  reminderFailed: 'We could not send that just now.',
  authorizedOn: (date) => `Authorized ${date}`,
  // C · the authorization's dates
  expiresWarning: (date, names) => `Your authorization expires on ${date}. If ${joinFirstNames(names)} ${(names || []).length > 1 ? "haven't" : "hasn't"} authorized by then, you'll need to authorize again. It only takes a moment.`,
  expired: (date) => `Your authorization expired on ${date} after 30 days. Authorize again to continue.`,
  // D · the co-borrower's approval
  coPopupBody: (primary) => `You and ${primary || 'your co-borrower'} are approved, subject to the conditions in your approval. ${primary || 'Your co-borrower'} reviews the SmartEstimate™ and signs first. Then you’ll read and sign the same agreement. We’ll email you when it’s your turn.`,  // the approved Joint Part 2 copy; the email is routes/borrower.js tellCoborrowersToSign
  // E · approved below the price asked
  belowHeading: 'We can approve a lower price',
  belowApplied: 'You applied for',
  belowApproved: 'We can approve up to',
  belowNotice: 'Your notice about this decision is in your Documents.',
  belowViewNotice: 'View your notice',
});
export const jointNames = joinFirstNames;
